import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Bell, Clock, Trash2, Calendar, User, Phone, CheckCircle, X, Edit, MessageSquare, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { encryptId } from '../utils/urlEncryption';

const PaymentReminders = ({ user }) => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, today, overdue, upcoming
  const [searchQuery, setSearchQuery] = useState(''); // Search state
  const [showSnoozeModal, setShowSnoozeModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [snoozeDays, setSnoozeDays] = useState(1);
  
  // Follow-up modal state
  const [followUpNotes, setFollowUpNotes] = useState('');
  const [followUpAmount, setFollowUpAmount] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('pending');

  useEffect(() => {
    loadReminders();
  }, []);

  const calculateFinancials = (record) => {
    let totalEstimated = 0;
    let totalAdvance = 0;
    let metalAdvance = 0;
    let amountToCollect = 0;
    
    // Calculate total advance from payments (common for all modules)
    const payments = record.advance_payments || [];
    totalAdvance = payments.reduce((sum, payment) => {
      if (payment.mode !== 'Metal Credit') {
        return sum + (parseFloat(payment.amount) || parseFloat(payment.inr) || 0);
      }
      return sum;
    }, 0);

    metalAdvance = payments.reduce((sum, payment) => {
      if (payment.mode === 'Metal Credit') {
        return sum + (parseFloat(payment.fine_wt) || 0);
      }
      return sum;
    }, 0);
    
    if (record.module === 'Order') {
      // For orders, use total_amount if available
      if (record.total_amount && parseFloat(record.total_amount) > 0) {
        totalEstimated = parseFloat(record.total_amount);
      } else {
        // Fallback: Calculate from items if total_amount not set
        const items = record.items || [];
        if (items.length > 0 && record.base24k_rate_ex_gst) {
          const totalFineWeight = items.reduce((sum, item) => {
            const weight = parseFloat(item.weight) || 0;
            const purity = parseFloat(item.purity) || 100;
            return sum + (weight * purity / 100);
          }, 0);
          let rate = parseFloat(record.base24k_rate_ex_gst) || 0;
          if (record.gst_input_style === 'EXCL' && rate > 0) {
            rate = rate * 1.03; // Add GST
          }
          totalEstimated = totalFineWeight * rate;
        }
      }
    } else if (record.module === 'Booking') {
      // For bookings, calculate from items and rate
      const items = record.items || [];
      if (items.length > 0 && record.base24k_rate_ex_gst) {
        const totalWeight = items.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0);
        let rate = parseFloat(record.base24k_rate_ex_gst) || 0;
        if (record.gst_input_style === 'EXCL' && rate > 0) {
          rate = rate * 1.03;
        }
        totalEstimated = totalWeight * rate;
      }
    } else if (record.module === 'Approval') {
      // For approvals, calculate from items and rate
      const items = record.items || [];
      if (items.length > 0 && record.base_24k_rate) {
        const totalWeight = items.reduce((sum, item) => sum + (parseFloat(item.weight) || 0), 0);
        let rate = parseFloat(record.base_24k_rate) || 0;
        if (record.gst_input_style === 'EXCL' && rate > 0) {
          rate = rate * 1.03;
        }
        totalEstimated = totalWeight * rate;
      }
    }
    
    // Amount to collect is the balance
    amountToCollect = Math.max(0, totalEstimated - totalAdvance);

    return {
      totalEstimated,
      totalAdvance,
      metalAdvance,
      balance: totalEstimated - totalAdvance,
      amountToCollect
    };
  };

  const loadReminders = async () => {
    try {
      setLoading(true);
      
      // Fetch all reminders from the reminders API
      const remindersRes = await axiosInstance.get('/reminders');
      
      const [bookingsRes, ordersRes, approvalsRes, customersRes] = await Promise.all([
        axiosInstance.get('/bookings'),
        axiosInstance.get('/orders'),
        axiosInstance.get('/approvals'),
        axiosInstance.get('/customers')
      ]);
      
      // Create a map of reminders by order_id for quick lookup
      const reminderMap = {};
      remindersRes.data.forEach(reminder => {
        reminderMap[reminder.order_id] = reminder;
      });
      
      // Filter bookings with reminders
      const bookingsWithReminders = bookingsRes.data
        .filter(booking => booking.reminder_date && booking.status !== 'Converted')
        .map(booking => ({
          ...booking,
          module: 'Booking',
          id: booking.booking_id,
          reminderData: reminderMap[booking.booking_id] // Add reminder data
        }));

      // Filter orders with reminders
      const ordersWithReminders = ordersRes.data
        .filter(order => order.reminder_date)
        .map(order => ({
          ...order,
          module: 'Order',
          id: order.order_id,
          booking_id: order.order_id,
          reminderData: reminderMap[order.order_id] // Add reminder data
        }));

      // Filter approvals with reminders
      const approvalsWithReminders = approvalsRes.data
        .filter(approval => approval.reminder_date && approval.status !== 'Returned')
        .map(approval => ({
          ...approval,
          module: 'Approval',
          id: approval.approval_id,
          booking_id: approval.approval_id,
          reminderData: reminderMap[approval.approval_id] // Add reminder data
        }));

      const allReminders = [...bookingsWithReminders, ...ordersWithReminders, ...approvalsWithReminders];

      const customers = customersRes.data;

      const enrichedReminders = allReminders.map(reminder => {
        const customer = customers.find(c => c.id === reminder.customer_id);
        const financials = calculateFinancials(reminder);
        
        return {
          ...reminder,
          customer_name: customer?.name || 'Unknown',
          customer_mobile: customer?.mobile || customer?.phone || 'N/A',
          ...financials,
          // Add target collection from reminder data if available
          targetCollection: reminder.reminderData?.amount_due || financials.amountToCollect
        };
      });

      enrichedReminders.sort((a, b) => new Date(a.reminder_date) - new Date(b.reminder_date));

      setReminders(enrichedReminders);
    } catch (err) {
      console.error('Error loading reminders:', err);
      toast.error('Failed to load payment reminders');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredReminders = () => {
    const today = new Date().toISOString().split('T')[0];
    
    let filtered = reminders;
    
    // Apply date filter
    switch (filter) {
      case 'today':
        filtered = reminders.filter(r => r.reminder_date === today);
        break;
      case 'overdue':
        filtered = reminders.filter(r => r.reminder_date < today);
        break;
      case 'upcoming':
        filtered = reminders.filter(r => r.reminder_date > today);
        break;
      default:
        filtered = reminders;
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(reminder => {
        const customerName = (reminder.customer_name || '').toLowerCase();
        const orderId = (reminder.order_id || reminder.booking_id || reminder.approval_id || '').toLowerCase();
        const module = (reminder.module || '').toLowerCase();
        const mobile = (reminder.customer_mobile || '').toString();
        const notes = (reminder.notes || '').toLowerCase();
        const customerId = (reminder.customer_id || '').toLowerCase();
        
        return (
          customerName.includes(query) ||
          orderId.includes(query) ||
          module.includes(query) ||
          mobile.includes(query) ||
          notes.includes(query) ||
          customerId.includes(query)
        );
      });
    }
    
    return filtered;
  };

  const getReminderStatus = (reminderDate) => {
    const today = new Date().toISOString().split('T')[0];
    if (reminderDate < today) return 'overdue';
    if (reminderDate === today) return 'today';
    return 'upcoming';
  };

  const handleFollowUp = async () => {
    if (!selectedReminder) return;
    if (!followUpNotes.trim()) {
      toast.error('Please add follow-up notes');
      return;
    }

    try {
      const followUpRecord = {
        date: new Date().toISOString(),
        notes: followUpNotes,
        amount_discussed: followUpAmount ? parseFloat(followUpAmount) : null,
        next_followup: followUpDate || null,
        payment_status: paymentStatus,
        by: user?.username || 'user'
      };

      const followUpHistory = selectedReminder.follow_up_history || [];
      followUpHistory.push(followUpRecord);

      const newReminderDate = followUpDate || null;

      if (selectedReminder.module === 'Booking') {
        const updatedBooking = { 
          ...selectedReminder, 
          follow_up_history: followUpHistory,
          reminder_date: newReminderDate
        };
        await axiosInstance.put(`/bookings/${selectedReminder.booking_id}`, updatedBooking);
      } else if (selectedReminder.module === 'Order') {
        const updatedOrder = { 
          ...selectedReminder, 
          follow_up_history: followUpHistory,
          reminder_date: newReminderDate
        };
        await axiosInstance.put(`/orders/${selectedReminder.order_id}`, updatedOrder);
      } else if (selectedReminder.module === 'Approval') {
        const updatedApproval = { 
          ...selectedReminder, 
          follow_up_history: followUpHistory,
          reminder_date: newReminderDate
        };
        await axiosInstance.put(`/approvals/${selectedReminder.approval_id}`, updatedApproval);
      }

      toast.success('Follow-up recorded successfully');
      setShowFollowUpModal(false);
      resetFollowUpForm();
      loadReminders();
    } catch (err) {
      console.error('Error recording follow-up:', err);
      toast.error('Failed to record follow-up');
    }
  };

  const resetFollowUpForm = () => {
    setFollowUpNotes('');
    setFollowUpAmount('');
    setFollowUpDate('');
    setPaymentStatus('pending');
    setSelectedReminder(null);
  };

  const handleSnooze = async () => {
    if (!selectedReminder || snoozeDays < 1) return;

    try {
      const newDate = new Date();
      newDate.setDate(newDate.getDate() + parseInt(snoozeDays));
      const newReminderDate = newDate.toISOString().split('T')[0];

      if (selectedReminder.module === 'Booking') {
        const updatedBooking = { ...selectedReminder, reminder_date: newReminderDate };
        await axiosInstance.put(`/bookings/${selectedReminder.booking_id}`, updatedBooking);
      } else if (selectedReminder.module === 'Order') {
        const updatedOrder = { ...selectedReminder, reminder_date: newReminderDate };
        await axiosInstance.put(`/orders/${selectedReminder.order_id}`, updatedOrder);
      } else if (selectedReminder.module === 'Approval') {
        const updatedApproval = { ...selectedReminder, reminder_date: newReminderDate };
        await axiosInstance.put(`/approvals/${selectedReminder.approval_id}`, updatedApproval);
      }

      toast.success(`Reminder snoozed for ${snoozeDays} day(s)`);
      setShowSnoozeModal(false);
      setSelectedReminder(null);
      setSnoozeDays(1);
      loadReminders();
    } catch (err) {
      console.error('Error snoozing reminder:', err);
      toast.error('Failed to snooze reminder');
    }
  };

  const handleDelete = async (reminder) => {
    if (!window.confirm(`Delete payment reminder for ${reminder.customer_name}?`)) return;

    try {
      // Only delete the reminder document - don't update the parent order/booking/approval
      // The reminders endpoint already handles cleanup
      await axiosInstance.delete(`/reminders/${reminder.reminder_id}`);

      toast.success('Payment reminder deleted');
      loadReminders();
    } catch (err) {
      console.error('Error deleting reminder:', err);
      toast.error('Failed to delete reminder');
    }
  };

  const handleMarkCompleted = async (reminder) => {
    if (!window.confirm(`Mark payment reminder as completed for ${reminder.customer_name}?`)) return;

    try {
      const completionRecord = {
        date: new Date().toISOString(),
        notes: 'Payment completed',
        payment_status: 'completed',
        by: user?.username || 'user'
      };

      const followUpHistory = reminder.follow_up_history || [];
      followUpHistory.push(completionRecord);

      if (reminder.module === 'Booking') {
        const updatedBooking = { 
          ...reminder, 
          reminder_date: null,
          follow_up_history: followUpHistory
        };
        await axiosInstance.put(`/bookings/${reminder.booking_id}`, updatedBooking);
      } else if (reminder.module === 'Order') {
        const updatedOrder = { 
          ...reminder, 
          reminder_date: null,
          follow_up_history: followUpHistory
        };
        await axiosInstance.put(`/orders/${reminder.order_id}`, updatedOrder);
      } else if (reminder.module === 'Approval') {
        const updatedApproval = { 
          ...reminder, 
          reminder_date: null,
          follow_up_history: followUpHistory
        };
        await axiosInstance.put(`/approvals/${reminder.approval_id}`, updatedApproval);
      }

      toast.success('Payment reminder marked as completed');
      loadReminders();
    } catch (err) {
      console.error('Error marking as completed:', err);
      toast.error('Failed to mark as completed');
    }
  };

  const filteredReminders = getFilteredReminders();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-20">
      <div className="max-w-7xl mx-auto p-3 sm:p-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go back"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <Bell className="w-6 h-6 text-amber-600" />
            <h1 className="text-2xl font-bold text-gray-800">Payment Reminders</h1>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
                filter === 'all'
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({reminders.length})
            </button>
            <button
              onClick={() => setFilter('overdue')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
                filter === 'overdue'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Overdue ({reminders.filter(r => getReminderStatus(r.reminder_date) === 'overdue').length})
            </button>
            <button
              onClick={() => setFilter('today')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
                filter === 'today'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Today ({reminders.filter(r => getReminderStatus(r.reminder_date) === 'today').length})
            </button>
            <button
              onClick={() => setFilter('upcoming')}
              className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${
                filter === 'upcoming'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Upcoming ({reminders.filter(r => getReminderStatus(r.reminder_date) === 'upcoming').length})
            </button>
          </div>

          {/* Search Bar */}
          <div className="mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by customer name, order ID, mobile, or notes..."
                className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-amber-500 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Reminders List */}
        {filteredReminders.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {searchQuery ? `No reminders found for "${searchQuery}"` : 'No payment reminders found'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredReminders.map((reminder) => {
              const status = getReminderStatus(reminder.reminder_date);
              const statusColors = {
                overdue: 'bg-red-50 border-red-300',
                today: 'bg-orange-50 border-orange-300',
                upcoming: 'bg-green-50 border-green-300'
              };

              const followUpCount = (reminder.follow_up_history || []).length;

              return (
                <div
                  key={reminder.id}
                  className={`bg-white border-2 rounded-xl p-4 ${statusColors[status]}`}
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                    {/* Left: Details */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="font-bold text-lg">{reminder.customer_name}</span>
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          reminder.module === 'Order' ? 'bg-blue-200 text-blue-800' :
                          reminder.module === 'Booking' ? 'bg-amber-200 text-amber-800' :
                          'bg-purple-200 text-purple-800'
                        }`}>
                          {reminder.module}
                        </span>
                        {followUpCount > 0 && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-bold">
                            {followUpCount} Follow-up{followUpCount > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <span className="font-semibold">{reminder.booking_id}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <a 
                            href={`tel:${reminder.customer_mobile}`}
                            className="font-semibold text-blue-600 hover:underline"
                          >
                            {reminder.customer_mobile}
                          </a>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span className="font-semibold">
                            Reminder: {new Date(reminder.reminder_date).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                          {status === 'overdue' && (
                            <span className="text-red-600 font-bold text-xs">(OVERDUE!)</span>
                          )}
                          {status === 'today' && (
                            <span className="text-orange-600 font-bold text-xs">(TODAY)</span>
                          )}
                        </div>
                        
                        {/* Financial Summary */}
                        <div className="mt-2 p-3 bg-white bg-opacity-70 rounded-lg border-2 border-current">
                          <div className="font-semibold text-xs mb-2 text-gray-700">Payment Summary:</div>
                          {reminder.totalEstimated > 0 && (
                            <div className="flex justify-between text-xs mb-1">
                              <span>Est. Total:</span>
                              <span className="font-bold">₹{reminder.totalEstimated.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-xs mb-1">
                            <span>Advance Paid:</span>
                            <span className="font-bold text-green-700">₹{reminder.totalAdvance.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                          </div>
                          {reminder.metalAdvance > 0 && (
                            <div className="flex justify-between text-xs mb-1">
                              <span>Metal Advance:</span>
                              <span className="font-bold">{reminder.metalAdvance.toFixed(2)}g</span>
                            </div>
                          )}
                          {reminder.targetCollection > 0 && (
                            <div className="flex justify-between text-xs pt-2 border-t-2 border-red-300 mt-2 bg-red-50 px-2 py-1 rounded">
                              <span className="font-bold text-red-700">🎯 Target Collection:</span>
                              <span className="font-bold text-xl text-red-700">₹{reminder.targetCollection.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex sm:flex-col gap-2">
                      <button
                        onClick={() => {
                          if (reminder.module === 'Booking') {
                            navigate(`/bookings/${encryptId(reminder.booking_id)}`);
                          } else if (reminder.module === 'Order') {
                            navigate(`/orders/${encryptId(reminder.order_id)}`);
                          } else if (reminder.module === 'Approval') {
                            navigate(`/approvals/${encryptId(reminder.approval_id)}`);
                          }
                        }}
                        className="flex-1 sm:flex-initial px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 whitespace-nowrap"
                      >
                        View
                      </button>
                      <button
                        onClick={() => {
                          setSelectedReminder(reminder);
                          setFollowUpDate(new Date(new Date().setDate(new Date().getDate() + 3)).toISOString().split('T')[0]);
                          setShowFollowUpModal(true);
                        }}
                        className="flex-1 sm:flex-initial px-3 py-2 bg-purple-600 text-white rounded-lg text-xs font-semibold hover:bg-purple-700 flex items-center gap-1 justify-center"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span className="hidden sm:inline">Follow-Up</span>
                      </button>
                      <button
                        onClick={() => handleMarkCompleted(reminder)}
                        className="flex-1 sm:flex-initial px-3 py-2 bg-green-600 text-white rounded-lg text-xs font-semibold hover:bg-green-700 flex items-center gap-1 justify-center"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span className="hidden sm:inline">Done</span>
                      </button>
                      <button
                        onClick={() => {
                          setSelectedReminder(reminder);
                          setShowSnoozeModal(true);
                        }}
                        className="flex-1 sm:flex-initial px-3 py-2 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 flex items-center gap-1 justify-center"
                      >
                        <Clock className="w-4 h-4" />
                        <span className="hidden sm:inline">Snooze</span>
                      </button>
                      <button
                        onClick={() => handleDelete(reminder)}
                        className="flex-1 sm:flex-initial px-3 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 flex items-center gap-1 justify-center"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Follow-Up Modal */}
        {showFollowUpModal && selectedReminder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Follow-Up: {selectedReminder.customer_name}</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Conversation Notes *</label>
                  <textarea
                    value={followUpNotes}
                    onChange={(e) => setFollowUpNotes(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg p-3 text-sm min-h-[100px]"
                    placeholder="What was discussed? Customer's response..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Status</label>
                  <select
                    value={paymentStatus}
                    onChange={(e) => setPaymentStatus(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg p-3 text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="partial">Paid Partial</option>
                    <option value="promised">Promised to Pay</option>
                    <option value="not_reachable">Not Reachable</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Amount Discussed (Optional)</label>
                  <input
                    type="number"
                    value={followUpAmount}
                    onChange={(e) => setFollowUpAmount(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg p-3 text-sm"
                    placeholder="Amount customer agreed to pay"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Next Follow-Up Date</label>
                  <input
                    type="date"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full border-2 border-gray-300 rounded-lg p-3 text-sm"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowFollowUpModal(false);
                    resetFollowUpForm();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleFollowUp}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700"
                >
                  Save Follow-Up
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Snooze Modal */}
        {showSnoozeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Snooze Reminder</h2>
              <p className="text-sm text-gray-600 mb-4">
                Postpone reminder for <strong>{selectedReminder?.customer_name}</strong>
              </p>
              
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Snooze for how many days?
              </label>
              <input
                type="number"
                min="1"
                value={snoozeDays}
                onChange={(e) => setSnoozeDays(e.target.value)}
                className="w-full border-2 border-gray-300 rounded-lg p-3 mb-4"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSnoozeModal(false);
                    setSelectedReminder(null);
                    setSnoozeDays(1);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSnooze}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700"
                >
                  Snooze
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReminders;
